import { useRef, useState } from 'react'
import { PanelError, isOutcomeUnknown, toPanelError } from '@/console/api/errors'
import { WorkflowRestartRequest, WorkflowRestartResponse, WorkflowRestartTarget } from '@/console/api/types'
import { requestWorkflowRestart } from '@/console/data/workflowRestart'
import { RestartStartInput, toStartRequest } from './workflowRestart'

/** 확인 다이얼로그에 올린 호출. 새로 시작이면 조건을, 이어받기면 대상 run 을 싣는다. */
export type RestartAsk = { kind: 'start'; input: RestartStartInput } | { kind: 'resume'; targets: WorkflowRestartTarget[] }

/** 호출 한 번의 기록. 실패해도 남겨 다음 판단의 근거로 쓴다. */
export type RestartCall = {
  seq: number
  ask: RestartAsk
  request: WorkflowRestartRequest
  response: WorkflowRestartResponse | null
  error: PanelError | null
  /** 응답 없이 끝나 서버가 cancel 했는지 알 수 없다. */
  outcomeUnknown: boolean
  /** 이 호출이 이어받기 몇 번째인지. 새로 시작이면 0. */
  resumeRound: number
}

export type WorkflowRestartController = {
  submitting: boolean
  pending: RestartAsk | null
  calls: RestartCall[]
  /** 마지막으로 보낸 새로 시작 조건. 같은 조건 재호출에 쓴다. */
  lastStartInput: RestartStartInput | null
  /** 지금 이어받기를 보내면 몇 번째가 되는지. */
  nextResumeRound: number
  ask: (ask: RestartAsk) => void
  confirm: () => void
  cancel: () => void
}

const toRequest = (ask: RestartAsk): WorkflowRestartRequest => (ask.kind === 'start' ? toStartRequest(ask.input) : { resumeTargets: ask.targets })

/**
 * 확인 → 호출 → 기록. 호출은 동기·최대 수 분이고 재시도하지 않는다. `sending` 은 리렌더 전에 들어온 두 번째 확인까지 막는다.
 * 이어받기 횟수는 새로 시작하면 0 으로 돌아간다 — runbook 6장의 다섯 번 상한은 한 번의 시작에 딸린 이어받기 수다.
 */
export const useWorkflowRestart = (): WorkflowRestartController => {
  const [pending, setPending] = useState<RestartAsk | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [calls, setCalls] = useState<RestartCall[]>([])
  const [lastStartInput, setLastStartInput] = useState<RestartStartInput | null>(null)
  const [resumeRounds, setResumeRounds] = useState(0)
  const sending = useRef(false)
  const seq = useRef(0)

  const send = async (ask: RestartAsk): Promise<void> => {
    if (sending.current) return
    sending.current = true
    setSubmitting(true)
    const request = toRequest(ask)
    const resumeRound = ask.kind === 'resume' ? resumeRounds + 1 : 0
    if (ask.kind === 'start') setLastStartInput(ask.input)
    setResumeRounds(resumeRound)
    seq.current += 1
    const base = { seq: seq.current, ask, request, resumeRound }
    try {
      const response = await requestWorkflowRestart(request)
      setCalls((prev) => [{ ...base, response, error: null, outcomeUnknown: false }, ...prev])
    } catch (cause) {
      setCalls((prev) => [{ ...base, response: null, error: toPanelError(cause), outcomeUnknown: isOutcomeUnknown(cause) }, ...prev])
    } finally {
      sending.current = false
      setSubmitting(false)
    }
  }

  return {
    submitting,
    pending,
    calls,
    lastStartInput,
    nextResumeRound: resumeRounds + 1,
    ask: (next) => {
      if (!submitting) setPending(next)
    },
    confirm: () => {
      const ask = pending
      setPending(null)
      if (ask) send(ask).catch(() => undefined)
    },
    cancel: () => setPending(null),
  }
}
